// src/helpers/foodBadgeHelper.js
// Determine which badges to show for food items based on user preferences

import { getDietStyleLabel } from './foodFilterBuilder';

/**
 * Get relevant badges to display for a food item
 * @param {Object} food - Food object with tags array
 * @param {Object} userFilter - Filter object from buildFoodFilterFromProfile
 * @returns {Array} Array of badge objects {type, label}
 */
export function getRelevantBadges(food, userFilter) {
    if (!food || !food.tagsV2) return [];
    if (!userFilter) return [];

    const badges = [];

    // 1. Diet compliance badge (if user has a diet style and food matches)
    if (userFilter.dietStyle && userFilter.dietStyle !== 'none') {
        const dietBadge = getDietComplianceBadge(food, userFilter.dietStyle);
        if (dietBadge) badges.push(dietBadge);
    }

    // 2. Allergen-safe badges (foods that are free from user's allergies)
    const allergenBadges = getAllergenSafeBadges(food, userFilter);
    badges.push(...allergenBadges);

    // 3. Convenience badges (foods that match user's convenience preferences)
    const convenienceBadges = getConvenienceBadges(food, userFilter);
    badges.push(...convenienceBadges.slice(0, 2)); // Max 2 convenience badges

    // Return max 4 badges total
    return badges.slice(0, 4);
}

/**
 * Get diet compliance badge if food matches user's diet style
 */
function getDietComplianceBadge(food, dietStyle) {
    const dietTagMap = {
        vegan: 'vegan',
        vegetarian: 'vegetarian',
        pescatarian: 'pescatarian',
    };

    const tagToCheck = dietTagMap[dietStyle];
    if (tagToCheck && food.tagsV2.includes(tagToCheck)) {
        return {
            type: 'diet',
            label: getDietStyleLabel(dietStyle),
        };
    }

    return null;
}

/**
 * Get allergen-safe badges for food
 */
function getAllergenSafeBadges(food, userFilter) {
    const badges = [];

    // Check common allergen-free tags
    const allergenMap = [
        { tag: 'glutenFree', label: 'Gluten-free' },
        { tag: 'dairyFree', label: 'Dairy-free' },
        { tag: 'nutFree', label: 'Nut-free' },
        { tag: 'eggFree', label: 'Egg-free' },
        { tag: 'soyFree', label: 'Soy-free' },
    ];

    for (const { tag, label } of allergenMap) {
        if (food.tagsV2.includes(tag)) {
            badges.push({ type: 'allergen', label });
        }
    }

    return badges;
}

/**
 * Get convenience badges for food
 */
function getConvenienceBadges(food, userFilter) {
    const badges = [];

    // Check if food matches user's convenience tags
    const convenienceMap = [
        { tag: 'quick', label: 'Quick' },
        { tag: 'onePot', label: 'One-pot' },
        { tag: 'batchFriendly', label: 'Batch-friendly' },
        { tag: 'minimalEquipment', label: 'Minimal equipment' },
    ];

    for (const { tag, label } of convenienceMap) {
        // Only show if user wants this AND food has it
        if (userFilter.prioritizeTags?.includes(tag) && food.tagsV2.includes(tag)) {
            badges.push({ type: 'convenience', label });
        }
    }

    return badges;
}

/**
 * Get bonus property badges (not related to preferences, just notable properties)
 */
export function getBonusPropertyBadges(food) {
    if (!food || !food.tagsV2) return [];

    const badges = [];

    const bonusMap = [
        { tag: 'wholegrain', label: 'Wholegrain' },
        { tag: 'highProtein', label: 'High-protein' },
        { tag: 'lowSodium', label: 'Low-sodium' },
        { tag: 'organic', label: 'Organic' },
    ];

    for (const { tag, label } of bonusMap) {
        if (food.tagsV2.includes(tag)) {
            badges.push({ type: 'bonus', label });
        }
    }

    return badges.slice(0, 2); // Max 2 bonus badges
}
